import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, FileText, Shield, Cpu, Building2, Layers, Network, CheckCircle2, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { API_URL } from '../api/apiUrl';

const resolveImg = (url) => (url && url.startsWith('/api') ? `${API_URL}${url}` : url);

const Blog = () => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState(null); // 'loading' | 'success' | 'error'
  const [newsletterMessage, setNewsletterMessage] = useState('');
  const [cmsPosts, setCmsPosts] = useState([]);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API_URL}/api/blog/posts`)
      .then(({ data }) => {
        if (!active) return;
        setCmsPosts(
          (data || []).map((p) => ({
            slug: p.slug,
            title: p.title,
            excerpt: p.excerpt,
            date: p.date,
            category: p.category,
            image: resolveImg(p.featured_image),
          }))
        );
      })
      .catch(() => {
        /* Blog CMS unavailable — fall back to static articles only. */
      });
    return () => {
      active = false;
    };
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    const email = newsletterEmail.trim();
    if (!email) return;
    setNewsletterStatus('loading');
    setNewsletterMessage('');
    try {
      const { data } = await axios.post(`${API_URL}/api/newsletter/subscribe`, {
        email,
        source: 'blog',
      });
      setNewsletterStatus('success');
      setNewsletterMessage(data.message || '¡Suscripción exitosa!');
      setNewsletterEmail('');
    } catch (error) {
      setNewsletterStatus('error');
      setNewsletterMessage(
        error.response?.data?.detail ||
          'No se pudo completar la suscripción. Intenta de nuevo.'
      );
    }
  };

  const posts = [
    {
      slug: '5-documentos-empresa-orden-2026',
      title: '5 Documentos que toda empresa debe tener en orden en 2026',
      excerpt: 'El entorno empresarial demanda agilidad administrativa sin comprometer la solidez legal. Estos son los 5 pilares documentales que garantizan la continuidad y protección del patrimonio de tu empresa en 2026.',
      icon: 'building',
      date: 'mayo 5, 2026',
      category: 'Empresarial'
    },
    {
      slug: 'que-hace-realmente-un-notario',
      title: '¿Qué hace realmente un notario? Más allá de la firma y el sello',
      excerpt: 'Más allá del acto formal de la firma y el sello, el trabajo notarial es un pilar de estabilidad social y económica. Descubre el verdadero alcance de la función notarial.',
      icon: 'document',
      date: 'mayo 5, 2026',
      category: 'Función Notarial'
    },
    {
      slug: 'contratos-inteligentes-practica-notarial-moderna',
      title: 'Contratos Inteligentes: ¿Cómo se integran en la práctica notarial moderna?',
      excerpt: 'Los Smart Contracts prometen eficiencia, transparencia y seguridad. Descubre cómo se integran estos códigos digitales en la práctica notarial tradicional.',
      icon: 'cpu',
      date: 'mayo 5, 2026',
      category: 'Smart Contracts'
    },
    {
      slug: 'blockchain-y-seguridad-juridica-certificacion-documentos',
      title: 'Blockchain y Seguridad Jurídica: El futuro de la certificación de documentos',
      excerpt: 'La tecnología blockchain promete revolucionar la certificación de documentos, ofreciendo nuevas capas de seguridad y eficiencia para la fe pública del siglo XXI.',
      icon: 'shield',
      date: 'mayo 5, 2026',
      category: 'Blockchain'
    },
    {
      slug: 'hitchain-y-notaria-90-nuevo-estandar-seguridad-juridica-digital',
      title: 'Hitchain y Notaría Pública 90 de Playa del Carmen: El Nuevo Estándar de la Seguridad Jurídica Digital',
      excerpt: 'La alianza entre Hitchain y la Notaría Pública 90 de Playa del Carmen demuestra que la fe pública y la tecnología de vanguardia juntas crean un blindaje jurídico sin precedentes.',
      icon: 'network',
      date: 'mayo 5, 2026',
      category: 'Legaltech & Blockchain'
    },
    {
      slug: 'la-era-de-la-notaria-4',
      title: 'La Era de la Notaría 4.0: Blockchain y Certeza Jurídica en Quintana Roo',
      excerpt: 'En un mundo donde la digitalización avanza a pasos agigantados, la fe pública no puede quedarse atrás. La Notaría Pública 90 se consolida como un referente de innovación al integrar herramientas tecnológicas de vanguardia.',
      icon: 'layers',
      date: 'abril 9, 2026',
      category: 'Tecnología Legal'
    }
  ];

  const iconMap = {
    document: FileText,
    shield: Shield,
    cpu: Cpu,
    building: Building2,
    layers: Layers,
    network: Network,
  };

  const allPosts = [...cmsPosts, ...posts];

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-notaria-primary to-notaria-secondary text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-cyan-400 rounded-full blur-3xl animate-pulse delay-700"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fade-in">
              Tips para la Era Legal Digital
            </h1>
            <p className="text-xl mb-8 animate-fade-in text-gray-100">
              Exploramos experiencias notariales y aprendizajes reales potenciados por Hitch, conectando certeza jurídica y tecnología.
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-20 bg-gradient-to-br from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {allPosts.map((post, index) => {
                const Icon = post.icon ? iconMap[post.icon] || FileText : null;
                return (
                <Card key={post.slug || index} className="overflow-hidden hover:shadow-2xl transition-all duration-300 group border-2 border-gray-100 hover:border-notaria-secondary">
                  <CardContent className="p-0">
                    {post.image ? (
                      <div className="h-64 overflow-hidden bg-gray-100">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      </div>
                    ) : (
                      <div className="h-64 relative overflow-hidden bg-gradient-to-br from-notaria-primary to-notaria-secondary flex items-center justify-center">
                        <div className="absolute inset-0 opacity-15">
                          <div className="absolute top-1/4 left-1/4 w-40 h-40 bg-white rounded-full blur-3xl"></div>
                          <div className="absolute bottom-1/4 right-1/4 w-40 h-40 bg-cyan-300 rounded-full blur-3xl"></div>
                        </div>
                        {Icon ? (
                          <Icon className="w-20 h-20 text-white relative z-10 group-hover:scale-110 transition-transform duration-500" strokeWidth={1.5} />
                        ) : null}
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>{post.date}</span>
                        </div>
                      </div>
                      <div className="inline-block bg-gray-100 text-notaria-secondary px-3 py-1 rounded-full text-xs font-semibold mb-3">
                        {post.category}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-notaria-secondary transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 mb-4 text-sm line-clamp-3">
                        {post.excerpt}
                      </p>
                      <Link to={`/blog/${post.slug}`}>
                        <Button variant="outline" className="text-notaria-secondary border-notaria-secondary hover:bg-notaria-secondary hover:text-white group/btn w-full">
                          Leer más
                          <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-gradient-to-br from-notaria-primary to-notaria-secondary text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Suscríbete a Nuestro Newsletter</h2>
            <p className="text-xl mb-8 text-gray-100">Recibe las últimas noticias, tips legales y artículos directamente en tu correo</p>
            <form
              onSubmit={handleNewsletterSubmit}
              className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto"
              data-testid="newsletter-form"
            >
              <input
                type="email"
                required
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                disabled={newsletterStatus === 'loading'}
                placeholder="Tu correo electrónico"
                data-testid="newsletter-email-input"
                className="flex-1 px-6 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-white disabled:opacity-60"
              />
              <Button
                type="submit"
                disabled={newsletterStatus === 'loading'}
                data-testid="newsletter-submit-btn"
                className="bg-white text-notaria-primary hover:bg-gray-100 px-8 py-3 font-semibold disabled:opacity-70"
              >
                {newsletterStatus === 'loading' ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Suscribirse'
                )}
              </Button>
            </form>
            {newsletterMessage && (
              <div
                data-testid="newsletter-message"
                className={`mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-medium ${
                  newsletterStatus === 'success'
                    ? 'bg-white/20 backdrop-blur border border-white/30 text-white'
                    : 'bg-red-500/30 border border-red-300/50 text-white'
                }`}
              >
                {newsletterStatus === 'success' && <CheckCircle2 className="w-5 h-5" />}
                <span>{newsletterMessage}</span>
              </div>
            )}
            <p className="text-xs text-gray-200 mt-6 opacity-80">
              Sin spam. Puedes darte de baja en cualquier momento.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
